import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { services } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Check, ArrowLeft, ArrowUpRight, Phone } from 'lucide-react';
import { CtaBanner } from '../components/site/BottomSections';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const related = services.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden bg-[#0f1a14]">
        <img src={service.hero} alt={service.title} className="absolute inset-0 h-full w-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1a14]/60 via-transparent to-[#0f1a14]/95" />
        <div className="relative mx-auto max-w-7xl h-full px-6 lg:px-10 flex flex-col justify-end pb-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-[#c9a56a] text-xs uppercase tracking-widest mb-6 hover:text-white transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> All Services
          </Link>
          <div className="flex items-center gap-4 mb-2">
            <div className="h-12 w-12 bg-[#c9a56a] grid place-items-center">
              <img src={service.icon} alt="" className="h-6 w-6" style={{ filter: 'brightness(0)' }} />
            </div>
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-[#c9a56a]" />
              <span className="text-[#c9a56a] text-xs uppercase tracking-[0.3em]">Ewen Service</span>
            </div>
          </div>
          <h1 className="font-serif text-white text-5xl md:text-7xl leading-[1.05] max-w-4xl">{service.title}</h1>
          <p className="text-white/80 mt-5 max-w-2xl text-lg italic font-serif">{service.tagline}</p>
        </div>
      </section>

      {/* Lead + intro */}
      <section className="bg-[#f6f1e7] py-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <p className="font-serif text-3xl md:text-4xl text-[#0f1a14] leading-[1.2]">{service.lead}</p>
          <div className="h-px w-16 bg-[#8a6b3a] my-10" />
          <div className="space-y-5 text-[#0f1a14]/75 leading-[1.9] text-lg">
            {service.intro.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 space-y-20">
          {service.sections.map((sec, i) => (
            <div key={i} className="grid md:grid-cols-3 gap-12 items-start">
              <div>
                <div className="font-serif text-6xl text-[#c9a56a]">{String(i + 1).padStart(2, '0')}</div>
                <h2 className="font-serif text-3xl text-[#0f1a14] mt-4 leading-tight">{sec.heading}</h2>
                <div className="h-px w-10 bg-[#8a6b3a] my-4" />
                <p className="text-[#0f1a14]/70 leading-relaxed">{sec.text}</p>
              </div>
              <div className="md:col-span-2">
                <ul className="grid sm:grid-cols-2 gap-4">
                  {sec.bullets.map((b, k) => (
                    <li key={k} className="flex items-start gap-3 bg-[#f6f1e7]/60 border border-[#0f1a14]/10 p-5 hover:border-[#8a6b3a] transition-colors">
                      <Check className="h-4 w-4 text-[#8a6b3a] shrink-0 mt-1" />
                      <span className="text-[#0f1a14]/80 text-sm leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#f6f1e7] py-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#8a6b3a]" />
            <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">Questions we hear</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0f1a14] leading-[1.1]">Good questions, honest answers.</h2>
          <Accordion type="single" collapsible className="space-y-3 mt-10">
            {service.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-[#0f1a14]/10 bg-white px-6">
                <AccordionTrigger className="text-left font-serif text-lg text-[#0f1a14] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#0f1a14]/75 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-14 bg-[#0f1a14] p-10 text-center">
            <div className="text-[#c9a56a] text-xs uppercase tracking-[0.3em]">Ready to start</div>
            <h3 className="font-serif text-3xl text-white mt-3">Talk to a real human, not a chat bot.</h3>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link to="/contact"><Button className="bg-[#c9a56a] text-[#0f1a14] hover:bg-[#b8945a] rounded-none px-8 py-6 tracking-wider">Book a Call</Button></Link>
              <a href="tel:+918526164444"><Button variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0f1a14] rounded-none px-8 py-6 tracking-wider"><Phone className="h-4 w-4 mr-2" /> +91 852-616-4444</Button></a>
            </div>
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <h2 className="font-serif text-3xl md:text-4xl text-[#0f1a14]">Explore other services</h2>
            <Link to="/services" className="inline-flex items-center gap-1 text-[#8a6b3a] hover:text-[#0f1a14] text-xs uppercase tracking-widest">All services <ArrowUpRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} className="group relative aspect-[4/5] overflow-hidden block">
                <img src={r.hero} alt={r.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f1a14] via-[#0f1a14]/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <div className="font-serif text-white text-lg leading-tight">{r.title}</div>
                  <div className="h-px w-8 bg-[#c9a56a] mt-2 group-hover:w-16 transition-all duration-500" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
